import moment from 'moment';
import 'moment/locale/fr';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAxios } from '../../utils/hook/useAxios';
import { getPushPermission, registerWebPush } from '../../utils/webpush';
import socket from '../../utils/socket';

moment.locale('fr');

const TYPE_ICONS = {
  reservation_new: { icon: 'bi-calendar-plus', color: 'text-primary' },
  reservation_accepted: { icon: 'bi-calendar-check', color: 'text-success' },
  reservation_cancelled: { icon: 'bi-calendar-x', color: 'text-danger' },
  reservation_paid: { icon: 'bi-credit-card', color: 'text-success' },
  message_new: { icon: 'bi-chat-dots', color: 'text-info' },
  payout: { icon: 'bi-cash-stack', color: 'text-success' },
  system: { icon: 'bi-bell', color: 'text-secondary' },
  checkin_submitted: { icon: 'bi-box-arrow-in-right', color: 'text-primary' },
  checkin_validated: { icon: 'bi-check2-circle', color: 'text-success' },
  checkin_refused: { icon: 'bi-x-circle', color: 'text-danger' },
  checkout_submitted: { icon: 'bi-box-arrow-right', color: 'text-warning' },
  checkout_validated: { icon: 'bi-flag-fill', color: 'text-success' },
  checkout_refused: { icon: 'bi-exclamation-circle', color: 'text-danger' },
  extra_charge: { icon: 'bi-credit-card-2-back', color: 'text-warning' },
};

function NotificationBell() {
  const axios = useAxios();
  const navigate = useNavigate();
  const dropRef = useRef(null);
  const userData = JSON.parse(window.localStorage.getItem('me') || 'null');
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pushPermission, setPushPermission] = useState(() => getPushPermission());
  const [pushLoading, setPushLoading] = useState(false);

  const unreadCount = notifs.filter((n) => !n.isRead).length;

  const fetchNotifs = async () => {
    if (!userData) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`/notifications/searchbyuserid/${userData.id}`);
      if (Array.isArray(data)) {
        setNotifs(data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      }
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => {
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  // Réception temps réel via socket
  useEffect(() => {
    const handleNewNotif = (notif) => {
      setNotifs((prev) => {
        // évite les doublons si le polling arrive juste après
        if (prev.some((n) => n.id === notif.id)) return prev;
        return [notif, ...prev];
      });
    };
    socket.on('notification', handleNewNotif);
    return () => socket.off('notification', handleNewNotif);
  }, []);

  // Ferme le dropdown si clic en dehors
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleEnablePush = async () => {
    if (!userData?.id) return;
    setPushLoading(true);
    const result = await registerWebPush(userData.id);
    setPushPermission(result === 'granted' ? 'granted' : Notification.permission);
    setPushLoading(false);
  };

  const handleMarkAllRead = async () => {
    if (!userData) return;
    try {
      await axios.put(`/notifications/user/${userData.id}/read-all`);
      setNotifs((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (_) {}
  };

  const handleClickNotif = async (notif) => {
    if (!notif.isRead) {
      await axios.put(`/notifications/${notif.id}/read`).catch(() => {});
      setNotifs((prev) => prev.map((n) => (n.id === notif.id ? { ...n, isRead: true } : n)));
    }
    setOpen(false);
    if (notif.link) navigate(notif.link);
  };

  if (!userData) return null;

  return (
    <div ref={dropRef} className="position-relative d-inline-block">
      {/* Bouton cloche */}
      <button
        className="btn btn-light btn-sm position-relative rounded-circle"
        style={{ width: 38, height: 38, padding: 0 }}
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
      >
        <i className="bi bi-bell-fill" style={{ fontSize: 16 }}></i>
        {unreadCount > 0 && (
          <span
            className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
            style={{ fontSize: 9, padding: '2px 5px', minWidth: 16 }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="dropdown-menu show shadow border-0 p-0"
          style={{
            right: 0,
            left: 'auto',
            top: '44px',
            width: 340,
            maxHeight: 460,
            overflowY: 'auto',
            position: 'absolute',
            zIndex: 1050,
          }}
        >
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
            <span className="fw-semibold" style={{ fontSize: 14 }}>
              Notifications
              {unreadCount > 0 && <span className="badge bg-danger ms-2">{unreadCount}</span>}
            </span>
            {unreadCount > 0 && (
              <button
                className="btn btn-link btn-sm p-0 text-muted"
                style={{ fontSize: 12 }}
                onClick={handleMarkAllRead}
              >
                Tout marquer lu
              </button>
            )}
          </div>

          {/* Bandeau activation notifications physiques */}
          {pushPermission !== 'granted' && pushPermission !== 'unsupported' && (
            <div
              className="px-3 py-2 border-bottom d-flex align-items-center gap-2"
              style={{ background: '#fff8e1', fontSize: 12 }}
            >
              <i className="bi bi-bell-slash text-warning flex-shrink-0"></i>
              <span className="flex-grow-1 text-muted">
                {pushPermission === 'denied'
                  ? 'Notifications bloquées — autorise-les dans les paramètres du navigateur'
                  : 'Reçois les messages même navigateur fermé'}
              </span>
              {pushPermission !== 'denied' && (
                <button
                  className="btn btn-warning btn-sm py-0 px-2 flex-shrink-0"
                  style={{ fontSize: 11 }}
                  onClick={handleEnablePush}
                  disabled={pushLoading}
                >
                  {pushLoading ? <span className="spinner-border spinner-border-sm" /> : 'Activer'}
                </button>
              )}
            </div>
          )}

          {/* Liste */}
          {loading ? (
            <div className="text-center p-3 text-muted small">Chargement…</div>
          ) : notifs.length === 0 ? (
            <div className="text-center p-4 text-muted">
              <i className="bi bi-bell-slash d-block mb-2" style={{ fontSize: 28 }}></i>
              <span style={{ fontSize: 13 }}>Aucune notification</span>
            </div>
          ) : (
            notifs.slice(0, 20).map((notif) => {
              const meta = TYPE_ICONS[notif.type] || TYPE_ICONS.system;
              return (
                <button
                  key={notif.id}
                  className={`w-100 text-start border-0 border-bottom px-3 py-2 d-flex gap-2 align-items-start ${notif.isRead ? 'bg-white' : 'bg-primary bg-opacity-10'}`}
                  style={{ fontSize: 13 }}
                  onClick={() => handleClickNotif(notif)}
                >
                  <i
                    className={`bi ${meta.icon} ${meta.color} flex-shrink-0 mt-1`}
                    style={{ fontSize: 16 }}
                  ></i>
                  <div className="flex-grow-1 overflow-hidden">
                    <p className="mb-0 fw-semibold text-truncate">{notif.Titre}</p>
                    <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                      {notif.message}
                    </p>
                    <p className="mb-0 text-muted" style={{ fontSize: 11 }}>
                      {moment(notif.createdAt).fromNow()}
                    </p>
                  </div>
                  {!notif.isRead && (
                    <span
                      className="bg-primary rounded-circle flex-shrink-0"
                      style={{ width: 8, height: 8, marginTop: 6 }}
                    ></span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
